// src/features/components/iconPacks/IconPackManager.ts
/**
 * IconPackManager - Updated for Reshot and local SVG support
 * 
 * Manages icon packs from multiple sources:
 * - Lucide icons (React components)
 * - Isometric SVGs
 * - Cloud provider icons
 * - Custom uploaded icons
 */

import { IconPack, IconDefinition, IconPackMetadata, IconPackConfig, IconPackLoader } from './types';

export class IconPackManager {
    private packs: Map<string, IconPack> = new Map();
    private packLoaders: Map<string, IconPackLoader> = new Map();
    private packLoadingState: Map<string, { loading: boolean; error?: string }> = new Map();
    private packMetadataStore: Map<string, { name: string; count: number; source: IconDefinition['source'] }> = new Map();
    private iconCache: Map<string, IconDefinition> = new Map();
    private config: IconPackConfig;

    constructor(config: IconPackConfig = {}) {
        this.config = {
            enableLazyLoading: config.enableLazyLoading ?? false,
            cacheIcons: config.cacheIcons ?? true,
            maxCacheSize: config.maxCacheSize ?? 1000
        };
    }

    // ===== PACK REGISTRATION =====

    /**
     * Register a single icon pack
     */
    registerPack(pack: IconPack): void {
        this.packs.set(pack.id, pack);

        // Cache icons if enabled
        if (this.config.cacheIcons) {
            pack.icons.forEach((icon) => {
                this.iconCache.set(icon.id, icon);
            });
        }
    }

    /**
     * Register multiple packs at once
     */
    registerPacks(packs: IconPack[]): void {
        packs.forEach((pack) => this.registerPack(pack));
    }

    /**
     * Register a lazy-loadable icon pack
     */
    registerPackLoader(packId: string, name: string, source: IconDefinition['source'], count: number, loader: IconPackLoader): void {
        this.packLoaders.set(packId, loader);
        this.packLoadingState.set(packId, { loading: false });
        this.packMetadataStore.set(packId, { name, count, source });
    }

    /**
     * Load a pack on-demand (if using lazy loading)
     */
    async loadPack(packId: string): Promise<IconPack | null> {
        // If already loaded, return it
        if (this.packs.has(packId)) {
            return this.packs.get(packId)!;
        }

        // Check if we have a loader for this pack
        const loader = this.packLoaders.get(packId);
        if (!loader) {
            console.error(`❌ No loader found for pack: ${packId}`);
            return null;
        }

        // Check if already loading
        const state = this.packLoadingState.get(packId);
        if (state?.loading) {
            // Wait for it to finish (simple polling - in production you'd use a promise queue)
            return new Promise((resolve) => {
                const checkInterval = setInterval(() => {
                    if (this.packs.has(packId)) {
                        clearInterval(checkInterval);
                        resolve(this.packs.get(packId)!);
                    }
                }, 100);
            });
        }

        // Mark as loading
        this.packLoadingState.set(packId, { loading: true });

        try {
            // Load the pack using the loader function
            const pack = await loader();

            // Register it
            this.registerPack(pack);

            // Update state
            this.packLoadingState.set(packId, { loading: false });

            return pack;
        } catch (error) {
            const errorMsg = error instanceof Error ? error.message : 'Unknown error';
            this.packLoadingState.set(packId, { loading: false, error: errorMsg });
            console.error(`❌ Failed to load pack ${packId}:`, error);
            return null;
        }
    }

    /**
     * Unregister a pack
     */
    unregisterPack(packId: string): void {
        const pack = this.packs.get(packId);
        if (pack) {
            pack.icons.forEach((icon) => {
                this.iconCache.delete(icon.id);
            });
            this.packs.delete(packId);
        }
    }

    // ===== PACK RETRIEVAL =====

    /**
     * Get all registered packs
     */
    getAllPacks(): IconPack[] {
        return Array.from(this.packs.values());
    }

    /**
     * Get pack by ID
     */
    getPack(packId: string): IconPack | undefined {
        return this.packs.get(packId);
    }

    /**
     * Get pack metadata (for displaying pack info)
     * Includes both loaded packs and registered loaders
     */
    getPackMetadata(): IconPackMetadata[] {
        const metadata: IconPackMetadata[] = [];

        // Add loaded packs
        this.packs.forEach((pack) => {
            metadata.push({
                id: pack.id,
                name: pack.name,
                count: pack.icons.length,
                loaded: true,
                loading: false,
                source: pack.source
            });
        });

        // Add registered loaders that haven't been loaded yet
        this.packLoaders.forEach((loader, packId) => {
            console.log('Checking loader for pack:', loader?.length);
            if (!this.packs.has(packId)) {
                const state = this.packLoadingState.get(packId);
                const storedMetadata = this.packMetadataStore.get(packId);

                metadata.push({
                    id: packId,
                    name: storedMetadata?.name ?? packId.charAt(0).toUpperCase() + packId.slice(1),
                    count: storedMetadata?.count ?? 0,
                    loaded: false,
                    loading: state?.loading ?? false,
                    error: state?.error,
                    source: storedMetadata?.source ?? 'cloud-provider'
                });
            }
        });

        return metadata;
    }

    // ===== ICON RETRIEVAL =====

    /**
     * Get all icons from all packs
     */
    getAllIcons(): IconDefinition[] {
        const allIcons: IconDefinition[] = [];
        this.packs.forEach((pack) => {
            allIcons.push(...pack.icons);
        });
        return allIcons;
    }

    /**
     * Get icons from a specific pack
     */
    getIconsByPack(packId: string): IconDefinition[] {
        const pack = this.packs.get(packId);
        return pack ? pack.icons : [];
    }

    /**
     * Get icons by category
     */
    getIconsByCategory(category: string): IconDefinition[] {
        const allIcons = this.getAllIcons();
        return allIcons.filter((icon) => icon.category === category);
    }

    /**
     * Get icon by ID (with caching)
     */
    getIconById(iconId: string): IconDefinition | undefined {
        return this.iconCache.get(iconId);
    }

    /**
     * Get all unique categories across all packs
     */
    getAllCategories(): string[] {
        const categories = new Set<string>();
        this.packs.forEach((pack) => {
            pack.categories.forEach((cat) => categories.add(cat));
        });
        return Array.from(categories).sort();
    }

    // ===== SEARCH =====

    /**
     * Search icons across all packs
     */
    searchIcons(query: string): IconDefinition[] {
        if (!query.trim()) {
            return this.getAllIcons();
        }

        const lowerQuery = query.toLowerCase();
        const allIcons = this.getAllIcons();

        return allIcons.filter((icon) => {
            return (
                icon.name.toLowerCase().includes(lowerQuery) ||
                icon.category.toLowerCase().includes(lowerQuery) ||
                icon.pack.toLowerCase().includes(lowerQuery) ||
                icon.keywords?.some((keyword) => keyword.toLowerCase().includes(lowerQuery))
            );
        });
    }

    /**
     * Filter icons by source
     */
    getIconsBySource(source: IconDefinition['source']): IconDefinition[] {
        const allIcons = this.getAllIcons();
        return allIcons.filter((icon) => icon.source === source);
    }

    /**
     * Get isometric icons only
     */
    getIsometricIcons(): IconDefinition[] {
        const allIcons = this.getAllIcons();
        return allIcons.filter((icon) => icon.isIsometric);
    }

    /**
     * Get statistics
     */
    getStats() {
        const allIcons = this.getAllIcons();
        const bySource: Record<string, number> = {};
        const byCategory: Record<string, number> = {};
        let isometricCount = 0;

        allIcons.forEach((icon) => {
            bySource[icon.source] = (bySource[icon.source] || 0) + 1;
            byCategory[icon.category] = (byCategory[icon.category] || 0) + 1;
            if (icon.isIsometric) isometricCount++;
        });

        return {
            totalPacks: this.packs.size,
            totalIcons: allIcons.length,
            isometricIcons: isometricCount,
            bySource,
            byCategory
        };
    }

    // ===== UTILITY =====

    /**
     * Clear all packs and cache
     */
    clear(): void {
        this.packs.clear();
        this.iconCache.clear();
    }

    /**
     * Log current state (for debugging)
     */
    debug(): void {
        const stats = this.getStats();
        console.group('🔍 IconPackManager Debug Info');
        console.log('Packs:', Array.from(this.packs.keys()));
        console.log('Stats:', stats);
        console.log('Cache size:', this.iconCache.size);
        console.groupEnd();
    }
}

// Export singleton instance with lazy loading enabled
export const iconPackManager = new IconPackManager({
    cacheIcons: true,
    enableLazyLoading: true
});